import React, { useEffect, useState } from 'react';
import './estadoCuenta.css';
import Select from 'react-select';
import { obtenerMembresia, obtenerMisFinanzas } from '../../services/obtenerMembresia';
import { pdf } from '@react-pdf/renderer';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { formatToMXN } from '../util/util';
import { Button } from '@chakra-ui/react';

const EstadoCuenta = ({ titularID }) => {

    const { t } = useTranslation();

    const [membresia, setMembresia] = useState(null);
    const [misFinanzas, setMisFinanzas] = useState(null);
    const [yearList, setYearList] = useState([]);

    const [year, setYear] = useState(null);
    const [quarter, setQuarter] = useState(null);
    const [initialBalance, setInitialBalance] = useState(0);

    const [ganancias, setGanancias] = useState(0);
    const [cargos, setCargos] = useState(0);
    const [dividendosAPagar, setDividendosAPagar] = useState(0);
    const [saldo, setSaldo] = useState(0);

    const [periodoMembresia, setPeriodoMembresia] = useState({})
    const [plusvalia, setPlusvalia] = useState(0);

    const [selectedTab, setSelectedTab] = useState('tab-info');


    const requestMembresia = async () => {
        try {
            const currentDate = new Date();
            const response = await obtenerMembresia(currentDate.toISOString(), titularID);
            setMembresia(response.data);
            const yearList = [];
            const oldestYear = response.data.oldestYear ? response.data.oldestYear : currentDate.getFullYear();
            const newestYear = response.data.newestYear ? response.data.newestYear : currentDate.getFullYear();
            for (let i = oldestYear; i <= newestYear; i++) {
                yearList.push({ value: i, label: i });
            }
            setYearList(yearList);
        } catch (error) {
            console.error(error);
        }
    }

    const generateEstadoCuentaPDF = async (data, periodo) => {
      const HEADER_HEIGHT = 60;
      const currentDate = new Date().toLocaleDateString();
    
      const styles = StyleSheet.create({
        /* ---------- Página ---------- */
        page: {
          paddingTop: HEADER_HEIGHT + 10, // deja espacio para el header fijo
          paddingHorizontal: 20,
          fontSize: 10,
          fontFamily: 'Helvetica',
        },
    
        /* ---------- Header ---------- */
        headerContainer: {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: HEADER_HEIGHT,
          backgroundColor: '#8C4F2B',    // marrón corporativo
          borderBottomWidth: 0,
          paddingHorizontal: 20,
          paddingVertical: 8,
        },
        header: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        },
        logo: {
          width: 60,
          height: 60,
          objectFit: 'contain',
        },
        date: {
          color: '#FFFFFF',
          fontSize: 10,
          textAlign: 'right',
        },
        reportTitle: {
          color: '#FFFFFF',
          fontSize: 13,
          fontWeight: 'bold',
        },
    
        /* ---------- Secciones y tablas (sin cambios) ---------- */
        sectionTitle: {
          fontSize: 12,
          fontWeight: 'bold',
          marginBottom: 10,
          marginTop: 20,
        },
        table: {
          display: 'table',
          width: '100%',
          borderStyle: 'solid',
          borderWidth: 1,
          borderColor: '#ddd',
          marginBottom: 20,
        },
        tableRow: {
          flexDirection: 'row',
        },
        tableCell: {
          flex: 1,
          padding: 5,
          borderStyle: 'solid',
          borderWidth: 1,
          borderColor: '#ddd',
        },
        tableHeader: {
          backgroundColor: '#f2f2f2',
          fontWeight: 'bold',
        },
      });
    
      /* ---------- Documento ---------- */
      const ReportDocument = (
        <Document>
          <Page size="A4" style={styles.page}>
            {/* Header fijo, de borde a borde */}
            <View fixed style={styles.headerContainer}>
              <View style={styles.header}>
                <Image style={styles.logo} src="/assets/Logo-sin-fondo-blanco.png" />
                <View>
                  <Text style={styles.date}>{`${t('REPORT_DATE')}${currentDate}`}</Text>
                  <Text style={styles.reportTitle}>
                    {t('PERIOD', { startDate: periodo.startDate, endDate: periodo.endDate })}
                  </Text>
                </View>
              </View>
            </View>
    
            {/* Secciones + tablas */}
            {data.map((section, index) => (
              <View key={index}>
                <Text style={styles.sectionTitle}>{section.title}</Text>
                <View style={styles.table}>
                  {/* Encabezado de tabla */}
                  <View style={[styles.tableRow, styles.tableHeader]}>
                    {section.headers.map((header, idx) => (
                      <Text key={idx} style={styles.tableCell}>
                        {header}
                      </Text>
                    ))}
                  </View>
                  {/* Filas */}
                  {section.rows.map((row, idx) => (
                    <View key={idx} style={styles.tableRow}>
                      {row.map((cell, cellIdx) => (
                        <Text key={cellIdx} style={styles.tableCell}>
                          {cell}
                        </Text>
                      ))}
                    </View>
                  ))}
                </View>
              </View>
            ))}
          </Page>
        </Document>
      );
    
      /* ---------- Generar & abrir PDF ---------- */
      const pdfBlob = await pdf(ReportDocument).toBlob();
      const pdfURL = URL.createObjectURL(pdfBlob);
      window.open(pdfURL, '_blank');
    };

    const handleGeneratePDF = () => {
        const periodo = {
            year: year ? year.value : new Date().getFullYear(),
            quarter: quarter ? quarter.value : 5, // 5 for "Todo el año
            startDate: quarter ? `01/${quarter.value === 1 ? '01' : quarter.value === 2 ? '04' : quarter.value === 3 ? '07' : '10'}/${year.value}` : '-',
            endDate: quarter ? `31/${quarter.value === 1 ? '03' : quarter.value === 2 ? '06' : quarter.value === 3 ? '09' : '12'}/${year.value}` : '-',
        }
        const reportData = [
          {
            title: t('MEMBERSHIP_INFO'),
            headers: [t('FIELD'), t('VALUE')],
            rows: [
                [t('DEVELOPMENT'), t('GLAMPING_CLUB')],
                [t('CABIN'), membresia ? membresia.tipoCabana : '-'],
                [t('MEMBERSHIP'), membresia ? membresia.membresia : '-'],
                [t('HOLDER'), membresia ? `${membresia.nombre} ${membresia.apellidos}` : '-'],
                [t('CO_HOLDER'), membresia && membresia.cotitular ? `${membresia.cotitular.nombre} ${membresia.cotitular.apellidos}` : '-'],
            ],
          },
          {
            title: t('INITIAL_BALANCE'),
            headers: [t('FIELD'), t('VALUE')],
            rows: [[t('INITIAL_BALANCE'), formatToMXN(initialBalance)]],
          },
          {
            title: t('EARNINGS'),
            headers: [t('CONCEPT'), t('AMOUNT')],
            rows: [
                [t('ACTIVE_INCOME'), formatToMXN(misFinanzas && misFinanzas.IA ? misFinanzas.IA.total : 0)],
                [t('PASSIVE_INCOME'), formatToMXN(misFinanzas && misFinanzas.IP ? misFinanzas.IP.total : 0)],
                [t('COMMISSIONS'), formatToMXN(misFinanzas && misFinanzas.CO ? misFinanzas.CO.total : 0)],
            ],
          },
            {
                title: t('CHARGES'),
                headers: [t('MAINTENANCE_COSTS'), t('AMOUNT')],
                rows: [
                    [t('MAINTENANCE_COSTS_LABEL'), formatToMXN(misFinanzas && misFinanzas.CM ? misFinanzas.CM.total : 0)],
                    [t('SURCHARGES'), formatToMXN(misFinanzas && misFinanzas.RE ? misFinanzas.RE.total : 0)],
                ],
            },
            {
                title: t('DIVIDENDS_TO_PAY'),
                headers: [t('CONCEPT'), t('AMOUNT')],
                rows: [[t('DIVIDENDS_TO_PAY'), formatToMXN(dividendosAPagar)]],
            },
            {
                title: t('DIVIDENDS_PAYMENT'),
                headers: [t('CONCEPT'), t('PAYMENT_AMOUNT')],
                rows: [
                    [t('DIVIDENDS_PAYMENT'), formatToMXN(misFinanzas && misFinanzas.PD ? misFinanzas.PD.total : 0)],
                    [t('CUSTOMER_PAYMENTS'), formatToMXN(misFinanzas && misFinanzas.PC ? misFinanzas.PC.total : 0)],
                    [t('BALANCE'), formatToMXN(saldo)],
                ],
            },
            {
                title: t('CAPITAL_GAIN'),
                headers: [t('CONCEPT'), t('PAYMENT_AMOUNT')],
                rows: [
                    [t('INITIAL_MEMBERSHIP_PRICE'), formatToMXN(membresia && membresia.precioCompraMembresia ? membresia.precioCompraMembresia : 0)],
                    [t('CURRENT_MEMBERSHIP_PRICE'), formatToMXN(periodoMembresia["precio"] ? periodoMembresia["precio"] : 0)],
                    [t('NET_CAPITAL_GAIN'), formatToMXN(plusvalia)],
                ],
            }
        ];
    
        generateEstadoCuentaPDF(reportData, periodo);
      };

    const requestMisFinanzas = async () => {
        try {
            const response = await obtenerMisFinanzas(quarter.value, year.value, titularID);
            let formatedFinanzas = {};
            if(response.data){
                formatedFinanzas = response.data.finanzas.reduce((acc, finanza) => {
                    acc[finanza.codigo] = finanza;
                    return acc;
                }, {});
            }

            const localTotalGanancias = (formatedFinanzas.IA ? formatedFinanzas.IA.total : 0) + 
                (formatedFinanzas.IP ? formatedFinanzas.IP.total : 0) + 
                (formatedFinanzas.CO ? formatedFinanzas.CO.total : 0);

            const localTotalCargos = (formatedFinanzas.CM ? formatedFinanzas.CM.total : 0) +
                (formatedFinanzas.RE ? formatedFinanzas.RE.total : 0);

            const localDividendosAPagar = localTotalGanancias - localTotalCargos;
            const localSaldo = response.data.initialBalance + localDividendosAPagar - 
                (formatedFinanzas.PD ? formatedFinanzas.PD.total : 0) + 
                (formatedFinanzas.PC ? formatedFinanzas.PC.total : 0)
            

            setGanancias(localTotalGanancias);
            setCargos(localTotalCargos);
            setDividendosAPagar(localDividendosAPagar);
            setSaldo(localSaldo);
            setPeriodoMembresia(response.data.periodoMembresia ? response.data.periodoMembresia : {});
            setInitialBalance(response.data.initialBalance);
            setMisFinanzas(formatedFinanzas);
            setPlusvalia(
                response.data.periodoMembresia && response.data.precioMembresia ?
                parseFloat(response.data.periodoMembresia.precio) - parseFloat(response.data.precioMembresia) :
                null
            );
            
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        requestMembresia();
    }, [])

    useEffect(() => {
        if(year && quarter){
            requestMisFinanzas();
        }
    }, [year, quarter])

    const quarterList = [
        { value: 1, label: '1er Trimestre (Ene-Mar)' },
        { value: 2, label: '2do Trimestre (Abr-Jun)' },
        { value: 3, label: '3er Trimestre (Jul-Sep)' },
        { value: 4, label: '4to Trimestre (Oct-Dic)' },
        { value: 5, label: 'Todo el año' }
    ];

    const handleWhatsAppRedirect = () => {
        const phoneNumber = membresia?.postVenta?.numero || ""; // Replace with the actual phone number
        const message = encodeURIComponent("Hola, necesito ayuda con mi membresía."); // Pre-fill message
        if (phoneNumber) {
            window.open(`https://wa.me/${phoneNumber}?text=${message}`, "_blank");
        } else {
            alert("Número de teléfono no disponible.");
        }
    };

    return (
        <div>
            <div className="container">
                {/* <div className="report-date">
                    <div className="report-date">{t('GENERATED_DATE', { date: '19/03/2025' })}</div>
                </div> */}

                <div className="section-title">{t('MEMBERSHIP_STATUS')}</div>
                
                <div className="tabs">
                    <button 
                        className={`tab${selectedTab === 'tab-info' ? ' active' : ''}`} 
                        onClick={() => setSelectedTab('tab-info')}>
                        {t('MEMBERSHIP_INFO')}
                    </button>
                    <button 
                        className={`tab${selectedTab === 'tab-account' ? ' active' : ''}`} 
                        onClick={() => setSelectedTab('tab-account')}>
                            {t('ACCOUNT_STATUS')}
                    </button>
                </div>

                {/* Tab: Información de Membresía */}
                <div id="tab-info" className={`tab-content ${selectedTab === 'tab-info' ? 'active' : ''}`}>
                    <h3 className="section-header">{t('MEMBERSHIP_INFO')}</h3>
                    <div className="account-info">
                        <div className="info-row">
                            <div className="info-label">{t('DEVELOPMENT')}</div>
                            <div className="info-value">{t('GLAMPING_CLUB')}</div>
                        </div>
                        <div className="info-row">
                        <div className="info-label">{t('CABIN')}</div>
                            <div className="info-value">
                                {
                                    membresia ? membresia.tipoCabana : '-'
                                }
                            </div>
                        </div>
                        <div className="info-row">
                            <div className="info-label">{t('MEMBERSHIP')}</div>
                            <div className="info-value">
                                {
                                    membresia ? membresia.membresia : '-'
                                }
                            </div>
                        </div>
                        <div className="info-row">
                        <div className="info-label">{t('HOLDER')}</div>
                            <div className="info-value">
                                {
                                    membresia ? `${membresia.nombre} ${membresia.apellidos}` : '-'
                                }
                            </div>
                        </div>
                        <div className="info-row">
                            <div className="info-label">{t('CO_HOLDER')}</div>
                            <div className="info-value">
                                {
                                    membresia && membresia.cotitular ? `${membresia.cotitular.nombre} ${membresia.cotitular.apellidos}` : '-'
                                }
                            </div>
                        </div>
                    </div>

                    <h3 className="section-header">{t('ACCOUNT_FOR_DEPOSIT')}</h3>
                    <div className="account-info">
                        <div className="info-row">
                            <div className="info-label">{t('BENEFICIARY_NAME')}</div>
                            <div className="info-value">
                                {
                                    membresia && membresia.cuentaBancaria ? membresia.cuentaBancaria.nombre : '-'
                                }
                            </div>
                        </div>
                        <div className="info-row">
                            <div className="info-label">{t('CLABE_ACCOUNT')}</div>
                            <div className="info-value">
                                {
                                    membresia && membresia.cuentaBancaria ? membresia.cuentaBancaria.clabe : '-'
                                }
                            </div>
                        </div>
                    </div>

                    <h3 className="section-header">{t('NEED_HELP')}</h3>
                    <div className="account-info">
                        <div className="info-row">
                        <div className="info-label">{t('POSTSALE_ADVISOR')}</div>
                            <div className="info-value">
                                {
                                    membresia && membresia.postVenta ? `${membresia.postVenta.nombre} ${membresia.postVenta.apellidos}` : '-'
                                }
                            </div>
                        </div>
                        <div className="info-row">
                        <div className="info-label">{t('ADVISOR_EMAIL')}</div>
                            <div className="info-value">
                                {
                                    membresia && membresia.postVenta ? membresia.postVenta.correo : '-'
                                }
                            </div>
                        </div>
                        <div className="info-row">
                        <div className="info-label">{t('CONTACT')}</div>
                            <div className="info-value">
                                <span className="btn" onClick={handleWhatsAppRedirect}>
                                    <span className="" onClick={handleWhatsAppRedirect}>{t('SEND_WHATSAPP_MESSAGE')}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tab: Estado de Cuenta */}
                <div id="tab-account" className={`tab-content ${selectedTab === 'tab-account' ? 'active' : ''}`}>
                    <div className="filter-row">
                        <div className="filter-group">
                            <label className="filter-label" htmlFor="year">{t('YEAR')}:</label>
                            {/* <select id="year" className="filter-select">
                                {
                                    yearList.map((year, ix) => {
                                        return (
                                            ix === 0 ?
                                                    <option value={year} key={ix} selected>{year}</option>
                                                :
                                                    <option value={year} key={ix}>{year}</option>
                                        )
                                    }
                                    )
                                }
                            </select> */}
                            <Select
                                options={yearList}
                                onChange={(selectedOption) => setYear(selectedOption)}
                                value={year}
                                placeholder={t('SELECT_YEAR')}
                            />
                        </div>
                        <div className="filter-group">
                            <label className="filter-label" htmlFor="trimester">{t('QUARTER')}:</label>
                            {/* <select id="trimester" className="filter-select">
                                <option value="1" selected>1er Trimestre (Ene-Mar)</option>
                                <option value="2">2do Trimestre (Abr-Jun)</option>
                                <option value="3">3er Trimestre (Jul-Sep)</option>
                                <option value="4">4to Trimestre (Oct-Dic)</option>
                                <option value="0">Todo el año</option>
                            </select> */}
                            <Select
                                options={quarterList}
                                onChange={(selectedOption) => setQuarter(selectedOption)}
                                value={quarter}
                                placeholder={t('SELECT_QUARTER')}
                            />
                        </div>
                    </div>

                    <div className="note">{t('NOTE')}</div>

                    <div className="account-info">
                        <div className="info-row">
                            <div className="info-label">{t('INITIAL_BALANCE')}</div>
                            <div className="info-value">
                                {formatToMXN(initialBalance)}
                                <span className="tooltip">
                                    <i className="info-icon">i</i>
                                    <span className="tooltiptext">{t('INITIAL_BALANCE_TOOLTIP')}</span>
                                </span>
                            </div>
                        </div>
                        {/* Este dato se calcula de acuerdo a los datos registrados en el módulo de contabilidad */}
                    </div>

                    <h3 className="section-header">{t('EARNINGS')}</h3>
                    <table className="account-table">
                        <tr>
                            <th>{t('CONCEPT')}</th>
                            <th style={{ textAlign: 'right' }}>{t('AMOUNT')}</th>
                        </tr>
                        <tr>
                            <td>
                                {t('ACTIVE_INCOME')}
                                <span className="tooltip">
                                    <i className="info-icon">i</i>
                                    <span className="tooltiptext">{t('ACTIVE_INCOME_TOOLTIP')}</span>
                                </span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                                {
                                    formatToMXN(misFinanzas && misFinanzas.IA ? misFinanzas.IA.total : 0)
                                }
                            </td>
                        </tr>
                        <tr>
                            <td>
                                {t('PASSIVE_INCOME')}
                                <span className="tooltip">
                                    <i className="info-icon">i</i>
                                    <span className="tooltiptext">{t('PASSIVE_INCOME_TOOLTIP')}</span>
                                </span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                                {
                                    formatToMXN(misFinanzas && misFinanzas.IP ? (misFinanzas.IP.total) : 0)
                                }
                            </td>
                        </tr>
                        <tr>
                            <td>
                                {t('COMMISSIONS')}
                                <span className="tooltip">
                                    <i className="info-icon">i</i>
                                    <span className="tooltiptext">{t('COMMISSIONS_TOOLTIP')}</span>
                                </span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                                {
                                    formatToMXN(misFinanzas && misFinanzas.CO ? (misFinanzas.CO.total) : 0)
                                }
                            </td>
                        </tr>
                        <tr className="total-row">
                            <td>{t('TOTAL_EARNINGS')}</td>
                            <td style={{ textAlign: 'right' }}>
                                {
                                    formatToMXN(ganancias)
                                }
                            </td>
                        </tr>
                    </table>

                    <h3 className="section-header">{t('CHARGES')}</h3>
                    <table className="account-table">
                        <tr>
                            <th>{t('CONCEPT')}</th>
                            <th style={{ textAlign: 'right' }}>
                                {t('AMOUNT')}
                            </th>
                        </tr>
                        <tr>
                            <td>
                                {t('MAINTENANCE_COSTS_LABEL')}
                                <span className="tooltip">
                                    <i className="info-icon">i</i>
                                    <span className="tooltiptext">{t('MAINTENANCE_COSTS_TOOLTIP')}</span>
                                </span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                                {
                                    formatToMXN(misFinanzas && misFinanzas.CM ? (misFinanzas.CM.total) : 0)
                                }
                            </td>
                        </tr>
                        <tr>
                            <td>
                                {t('SURCHARGES')}
                                <span className="tooltip">
                                    <i className="info-icon">i</i>
                                    <span className="tooltiptext">{t('SURCHARGES_TOOLTIP')}</span>
                                </span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                                {
                                    formatToMXN(misFinanzas && misFinanzas.RE ? (misFinanzas.RE.total) : 0)
                                }
                            </td>
                        </tr>
                        <tr className="total-row">
                            <td>{t('TOTAL_CHARGES')}</td>
                            <td style={{ textAlign: 'right' }}>
                                {
                                    formatToMXN(cargos)
                                }
                            </td>
                        </tr>
                    </table>

                    <h3 className="section-header">{t('DIVIDENDS_TO_PAY')}</h3>
                    <table className="account-table">
                        <tr>
                            <th>{t('CONCEPT')}</th>
                            <th style={{ textAlign: 'right' }}>{t('AMOUNT')}</th>
                        </tr>
                        <tr>
                            <td>
                                {t('DIVIDENDS_TO_PAY')}
                                <span className="tooltip">
                                    <i className="info-icon">i</i>
                                    <span className="tooltiptext">{t('DIVIDENDS_TO_PAY_TOOLTIP')}</span>
                                </span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                                {
                                    formatToMXN(dividendosAPagar)
                                }
                            </td>
                        </tr>
                    </table>

                    <h3 className="section-header">{t('DIVIDENDS_PAYMENT')}</h3>
                    <table className="account-table">
                        <tr>
                            <th>{t('CONCEPT')}</th>
                            <th style={{ textAlign: 'right' }}>{t('PAYMENT_AMOUNT')}</th>
                        </tr>
                        <tr>
                            <td>
                                {t('DIVIDENDS_PAYMENT')}
                                <span className="tooltip">
                                    <i className="info-icon">i</i>
                                    <span className="tooltiptext">{t('DIVIDENDS_PAYMENT_TOOLTIP')}</span>
                                </span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                                {
                                    formatToMXN(misFinanzas && misFinanzas.PD ? (misFinanzas.PD.total) : 0)
                                }
                            </td>
                        </tr>
                        <tr>
                            <td>
                                {t('CUSTOMER_PAYMENTS')}
                                <span className="tooltip">
                                    <i className="info-icon">i</i>
                                    <span className="tooltiptext">{t('CUSTOMER_PAYMENTS_TOOLTIP')}</span>
                                </span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                                {
                                    formatToMXN(misFinanzas && misFinanzas.PC ? (misFinanzas.PC.total) : 0)
                                }
                            </td>
                        </tr>       
                        <tr className="total-row">
                            <td>{t('BALANCE')}</td>
                            <td style={{ textAlign: 'right' }}>
                                {
                                    formatToMXN(saldo)
                                }
                            </td>
                        </tr>
                    </table>

                    <h3 className="section-header">{t('CAPITAL_GAIN')}</h3>
                    <table className="account-table">
                        <tr>
                            <th>{t('CONCEPT')}</th>
                            <th style={{ textAlign: 'right' }}>{t('PAYMENT_AMOUNT')}</th>
                        </tr>
                        <tr>
                            <td>
                                {t('INITIAL_MEMBERSHIP_PRICE')}
                                <span className="tooltip">
                                    <i className="info-icon">i</i>
                                    <span className="tooltiptext">{t('ORIGINAL_VALUE_TOOLTIP')}</span>
                                </span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                                {
                                    formatToMXN(membresia && membresia.precioCompraMembresia	 ? membresia.precioCompraMembresia	 : 0)
                                }
                            </td>
                        </tr>
                        <tr>
                            <td>
                                {t('CURRENT_MEMBERSHIP_PRICE')}
                                <span className="tooltip">
                                    <i className="info-icon">i</i>
                                    <span className="tooltiptext">{t('CURRENT_VALUE_TOOLTIP')}</span>
                                </span>
                            </td>
                            <td style={{ textAlign: 'right' }}>{formatToMXN(periodoMembresia["precio"] ? periodoMembresia["precio"]  : 0)}</td>
                        </tr>
                        <tr className="total-row">
                            <td>
                                {t('NET_CAPITAL_GAIN')}
                                <span className="tooltip">
                                    <i className="info-icon">i</i>
                                    <span className="tooltiptext">{t('NET_CAPITAL_GAIN_TOOLTIP')}</span>
                                </span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                                {
                                    formatToMXN(plusvalia)
                                }
                            </td>
                        </tr>
                    </table>
                    <Button
                        colorScheme="yellow"
                        my={5}
                        onClick={handleGeneratePDF}
                    >
                        {t('DOWNLOAD_PDF')}
                    </Button>
                    {/* <div className="action-row">
                        <a href="#" className="btn">Descargar en PDF</a>
                        <a href="#" className="btn">Descargar en Excel</a>
                    </div> */}
                </div>
            </div>
        </div>
    );
};

export default EstadoCuenta;